import { memo, useMemo } from 'react'

import { formatMoney } from 'accounting'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faTimes } from '@fortawesome/pro-solid-svg-icons'

import { Box, Table, Text } from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import StatusBadge from '@components/StatusBadge'

import useAccountingIntegration from '@hooks/useAccountingIntegration'

import type { CreditNoteModel, InvoiceModel } from '@models/types'

import CogOptions from '../CogOptions'
import CreditNoteActionButtons from '../CreditNoteActionButton'
import InvoiceActionButtons from '../InvoiceActionButton'

const buildColumns = (
  accountingIntegrationPayload: ReturnType<typeof useAccountingIntegration>,
) => {
  const { hasActiveSession } = accountingIntegrationPayload

  return [
    {
      title: 'Campaign',
      dataKey: 'address',
      cellProps: {
        position: 'sticky',
        left: 0,
        width: 250,
      },
      render: (address: string, item: InvoiceModel) => (
        <Text variant="ellipsis" width={260}>
          {address || item.reference}
        </Text>
      ),
    },
    {
      title: 'Type',
      dataKey: 'type',
      cellProps: {
        textAlign: 'centre',
      },
      render: (entityType: string) => (
        // @ts-expect-error TS2741
        <StatusBadge
          color={entityType === 'CreditNote' ? 'green' : 'orange'}
          text={entityType === 'CreditNote' ? 'Credit' : 'Invoice'}
        />
      ),
    },
    {
      title: 'ID #',
      dataKey: 'id',
      cellProps: {
        textAlign: 'right',
      },
    },
    {
      title: 'Created',
      dataKey: 'dates',
      render: (dates: object) => {
        const createdDate = digObject(dates, 'created.date')
        return (
          <Text variant="ellipsis" width={100}>
            {createdDate}
          </Text>
        )
      },
    },
    {
      title: 'Contact',
      dataKey: 'contact_name',
      render: (title: string, item: InvoiceModel) => (
        <Text variant="ellipsis" width={130}>
          {title || item.debtor_name}
        </Text>
      ),
    },
    {
      title: 'Reference',
      dataKey: 'reference',
      cellProps: {
        width: 100,
      },
      render: (reference?: string) => (
        <Text variant="ellipsis" width={200}>
          {reference || 'No Reference'}
        </Text>
      ),
    },
    {
      title: 'Total',
      dataKey: 'total',
      cellProps: {
        textAlign: 'right',
      },
      render: (total: string) => <Text>{total ? formatMoney(total) : '$0.00'}</Text>,
    },
    {
      title: 'Paid',
      dataKey: 'paid',
      cellProps: {
        textAlign: 'center',
      },
      render: (paid: boolean, item: InvoiceModel) => {
        const type = digObject(item, 'type', '')

        if (type === 'CreditNote'){
          return <Text color="green">N/A</Text>
        }
        return <FontAwesomeIcon icon={paid ? faCheck : faTimes} />
      },
    },
    {
      title: 'Approved',
      dataKey: 'approved',
      cellProps: {
        textAlign: 'center',
      },
      render: (approved: boolean, item: InvoiceModel) => {
        const type = digObject(item, 'type', '')

        if (type === 'CreditNote'){
          return <Text color="green">N/A</Text>
        }
        return <FontAwesomeIcon icon={approved ? faCheck : faTimes} />
      },
    },
    {
      title: 'Void',
      dataKey: 'void',
      cellProps: {
        textAlign: 'center',
      },
      render: (dataKey: boolean, item: InvoiceModel) => {
        const type = digObject(item, 'type', '')

        if (type === 'CreditNote'){
          return <Text color="green">N/A</Text>
        }
        return <FontAwesomeIcon icon={dataKey ? faCheck : faTimes} />
      },
    },
    {
      title: 'PO #',
      dataKey: 'purchase_order',
      cellProps: {
        width: 'auto',
      },
      render: (purchase_order: string) => (
        <Text
          color="faintGrey"
          variant="ellipsis"
          maxWidth={150}
        >
          {purchase_order || 'No PO#'}
        </Text>
      ),
    },
    {
      title: '',
      dataKey: 'campaign_id',
      cellProps: {
        textAlign: 'center',
        minWidth: hasActiveSession ? 200 : 'auto',
        maxWidth: hasActiveSession ? 200 : 'auto',
      },
      render: (campaign_id: number, item: InvoiceModel) => {
        const type = digObject(item, 'type', '')

        if (type === 'Invoice'){
          return (
            <Box>
              <InvoiceActionButtons
                accountingPayload={accountingIntegrationPayload}
                invoice={item}
              />
            </Box>
          )
        }

        return (
          <Box>
            <CreditNoteActionButtons
              accountingPayload={accountingIntegrationPayload}
              // @ts-expect-error TS2322
              creditNote={item}
            />
          </Box>
        )
      },
    },
    {
      title: 'Actions',
      dataKey: 'cache_key',
      cellProps: {
        textAlign: 'center',
        width: 40,
      },
      render: (cache_key: number, item: InvoiceModel) => (
        <CogOptions
          item={item}
        />
      ),
    },
  ]
}

type InvoicesTableProps = {
  accountingItems: (InvoiceModel | CreditNoteModel)[],
  accountingIntegrationPayload: ReturnType<typeof useAccountingIntegration>,
}

const TableView = (props: InvoicesTableProps) => {
  const { accountingItems, accountingIntegrationPayload } = props

  const { hasActiveSession } = accountingIntegrationPayload

  const memoColumns = useMemo(() => buildColumns(
    accountingIntegrationPayload,
  ), [accountingItems.length, hasActiveSession])

  return (
    <Table
      boxProps={{ border: 'none', maxHeight: 800, overflowY: 'auto' }}
      // @ts-expect-error TS2322
      columns={memoColumns}
      // @ts-expect-error TS2322
      data={accountingItems}
      scroll={{ x: 1600 }}
      stickyHeader
    />
  )
}

export default memo(TableView)
